/* Pook — theme behaviour.
   Everything the React components did with hooks, done with the platform.
   The Hydrogen app never reached for a motion library: it was all
   IntersectionObserver, rAF and refs, which is why this ports cleanly.
   The global overlays (search, subscribe, cart) follow root.tsx: mounted
   once, opened by a window event from anywhere. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var on = function (el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); };
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]; }); };
  var money = function (cents) {
    var fmt = document.body.getAttribute('data-money') || '${{amount}}';
    return fmt.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  };

  /* ── Tracking ──────────────────────────────────────────────────────
     One call site for every marketing tag. Events use GA4's names and item
     shape and go to window.dataLayer, which is what a GTM container reads;
     when GA4 is loaded directly (no container) the same event is sent through
     gtag, and the equivalent standard event is mirrored to Meta, TikTok and
     Pinterest. Nothing here assumes any tag is present — with no IDs
     configured this is a few pushes onto an array nobody reads.

     Checkout and the thank-you page belong to Shopify and cannot run theme
     code, so `purchase` lives in the Custom Pixel (docs/shopify-custom-pixel.js). */
  var MIRROR = {
    view_item:      { fb: 'ViewContent',   tt: 'ViewContent',    pin: 'pagevisit' },
    view_item_list: { fb: 'ViewCategory' },
    add_to_cart:    { fb: 'AddToCart',     tt: 'AddToCart',      pin: 'addtocart' },
    begin_checkout: { fb: 'InitiateCheckout', tt: 'InitiateCheckout' },
    search:         { fb: 'Search',        tt: 'Search',         pin: 'search' },
    sign_up:        { fb: 'Lead',          tt: 'SubmitForm',     pin: 'lead' }
  };
  function track(name, params) {
    var p = params || {};
    try {
      window.dataLayer = window.dataLayer || [];
      /* Null the previous ecommerce object first, or GTM merges the two. */
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(Object.assign({ event: name }, p));
      if (window.gtag && !window.google_tag_manager) window.gtag('event', name, p);
      var m = MIRROR[name]; if (!m) return;
      var value = p.value, currency = p.currency, ids = (p.items || []).map(function (i) { return i.item_id; });
      if (window.fbq && m.fb) window.fbq('track', m.fb, {
        content_type: 'product', content_ids: ids,
        content_name: p.items && p.items[0] ? p.items[0].item_name : undefined,
        value: value, currency: currency, search_string: p.search_term
      });
      if (window.ttq && m.tt) window.ttq.track(m.tt, {
        contents: (p.items || []).map(function (i) { return { content_id: String(i.item_id), content_name: i.item_name, quantity: i.quantity, price: i.price }; }),
        value: value, currency: currency, query: p.search_term
      });
      if (window.pintrk && m.pin) window.pintrk('track', m.pin, {
        value: value, currency: currency, line_items: (p.items || []).map(function (i) { return { product_id: String(i.item_id), product_name: i.item_name, product_price: i.price, product_quantity: i.quantity }; })
      });
    } catch (e) {}
  }
  window.pookTrack = track;

  function currencyCode() {
    try { if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) return window.Shopify.currency.active; } catch (e) {}
    var tag = $('[data-pook-ecommerce]');
    if (tag) { try { return JSON.parse(tag.textContent).currency || null; } catch (e) {} }
    return null;
  }

  function lineToItem(it) {
    return { item_id: it.sku || String(it.variant_id || it.id), item_name: it.product_title || it.title,
      item_variant: it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : undefined,
      item_brand: it.vendor, price: (it.final_price != null ? it.final_price : it.price) / 100, quantity: it.quantity || 1 };
  }

  /* Page-level events, from the JSON the Liquid side rendered. */
  function pageView() {
    var tag = $('[data-pook-ecommerce]'); if (!tag) return;
    var d; try { d = JSON.parse(tag.textContent); } catch (e) { return; }
    var name = d.event; delete d.event;
    if (name === 'search' && !d.search_term) return;
    track(name, d);
    /* A product card click, so the list a purchase came from is attributable. */
    on(document, 'click', function (e) {
      var card = e.target.closest && e.target.closest('.pcard, .look, .tile');
      if (!card || !d.items) return;
      var href = card.getAttribute('href') || '';
      var hit = d.items.filter(function (i) { return href.indexOf(encodeURIComponent(i.item_name)) > -1; })[0];
      track('select_item', { item_list_id: d.item_list_id, item_list_name: d.item_list_name, items: [hit || d.items[0]] });
    });
  }

  /* Campaign attribution: the first click a visitor arrives on wins, and the
     values ride along to the order so admin shows what produced it. */
  function attribution() {
    var KEY = 'pook:attribution', KEYS = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','gclid','fbclid','ttclid','msclkid','irclickid'];
    var params = new URLSearchParams(location.search), found = {};
    KEYS.forEach(function (k) { var v = params.get(k); if (v) found[k] = v.slice(0, 120); });
    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) {}
    if (Object.keys(found).length) {
      if (!saved) { found.landing_page = location.pathname; found.referrer = document.referrer.slice(0, 180); saved = found;
        try { sessionStorage.setItem(KEY, JSON.stringify(saved)); } catch (e) {} }
    }
    window.pookAttribution = saved;
    if (!saved || document.body.getAttribute('data-utm-order') !== 'true') return;
    /* Written once per session, and again after the cart is first filled. */
    var attrs = {}; Object.keys(saved).forEach(function (k) { attrs[k] = saved[k]; });
    window.pookWriteAttribution = function () {
      try { if (sessionStorage.getItem(KEY + ':written') === '1') return; } catch (e) {}
      fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attrs }) })
        .then(function () { try { sessionStorage.setItem(KEY + ':written', '1'); } catch (e) {} }).catch(function () {});
    };
  }

  /* Leaving for checkout — the last event a theme can see. */
  function checkoutIntent() {
    on(document, 'click', function (e) {
      var t = e.target.closest && e.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!t) return;
      var c = window.pookCartSnapshot && window.pookCartSnapshot();
      if (c && c.item_count) track('begin_checkout', { currency: c.currency, value: c.total_price / 100, items: c.items.map(lineToItem) });
      else fetch('/cart.js').then(function (r) { return r.json(); }).then(function (d) {
        if (d.item_count) track('begin_checkout', { currency: d.currency, value: d.total_price / 100, items: d.items.map(lineToItem) });
      }).catch(function () {});
      if (window.pookWriteAttribution) window.pookWriteAttribution();
    }, true);
  }

  /* ── Announcement bar ─────────────────────────────────────────── */
  function banner() {
    var el = $('[data-banner]'); if (!el) return;
    var KEY = 'pook2-banner-dismissed-v1', H = 38, root = document.documentElement;
    var dismissed = false;
    try { dismissed = localStorage.getItem(KEY) === '1'; } catch (e) {}
    if (dismissed) { el.hidden = true; root.style.setProperty('--banner-h', '0px'); return; }
    root.style.setProperty('--banner-h', H + 'px');
    var msgs = $$('[data-banner-msg]', el);
    if (msgs.length > 1 && !reduce) {
      var i = 0;
      setInterval(function () { msgs[i].classList.remove('is-active'); i = (i + 1) % msgs.length; msgs[i].classList.add('is-active'); }, 7000);
    }
    on($('[data-banner-close]', el), 'click', function () {
      el.classList.add('is-dismissing');
      root.style.setProperty('--banner-h', '0px');
      try { localStorage.setItem(KEY, '1'); } catch (e) {}
      setTimeout(function () { el.hidden = true; }, 320);
    });
  }

  /* ── Mobile menu ──────────────────────────────────────────────── */
  function menu() {
    var btn = $('[data-burger]'), sheet = $('[data-mobile-menu]'); if (!btn || !sheet) return;
    function set(open) { btn.setAttribute('aria-expanded', String(open)); btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu'); sheet.hidden = !open; }
    on(btn, 'click', function () { set(btn.getAttribute('aria-expanded') !== 'true'); });
    on(window, 'keydown', function (e) { if (e.key === 'Escape' && !sheet.hidden) set(false); });
    on(window, 'resize', function () { if (innerWidth >= 900 && !sheet.hidden) set(false); });
  }

  /* ── Reveal on enter ──────────────────────────────────────────── */
  function reveals() {
    var els = $$('.reveal'); if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });
    els.forEach(function (el) { el.classList.add('armed'); io.observe(el); });
    setTimeout(function () { els.forEach(function (el) { el.classList.add('is-in'); }); }, 2500);
  }

  /* ── Lazy blur-up images (FadeInImage) ────────────────────────── */
  function fadeImages() {
    function arm(img) {
      if (img.complete && img.naturalWidth > 0) { img.classList.add('is-loaded'); return; }
      on(img, 'load', function () { img.classList.add('is-loaded'); });
      on(img, 'error', function () { img.classList.add('is-loaded'); });
    }
    $$('img[data-fade]').forEach(arm);
    window.pookArmFade = function (root) { $$('img[data-fade]', root).forEach(arm); };
  }

  /* ── Body-part tiles ──────────────────────────────────────────── */
  var idle = window.requestIdleCallback ? function (f) { requestIdleCallback(f, { timeout: 2000 }); } : function (f) { setTimeout(f, 300); };

  function tiles() {
    $$('[data-tile]').forEach(function (tile) {
      var stage = $('[data-tile-stage]', tile), label = $('[data-tile-label]', tile);
      /* The wood photograph behind the product — same deal as the frames. */
      var wood = $('.tile__wood[data-wood]', tile);
      if (wood) {
        var paint = function () { wood.src = wood.getAttribute('data-wood'); wood.removeAttribute('data-wood'); };
        if ('IntersectionObserver' in window) {
          var wio = new IntersectionObserver(function (entries, obs) {
            if (!entries.some(function (e) { return e.isIntersecting; })) return;
            obs.disconnect(); paint();
            /* Generous margin: the texture should be decoded before the tile
               is on screen, not start loading as it arrives. */
          }, { rootMargin: '800px 0px' });
          wio.observe(tile);
        } else paint();
      }
      /* The remaining cross-fade frames are data, not markup — see the comment
         in sections/body-part-tiles.liquid. Build them once the page has
         loaded and the browser is idle, so they never compete with the hero. */
      if (stage && stage.getAttribute('data-frames') && !reduce) {
        var spec = stage.getAttribute('data-frames');
        stage.removeAttribute('data-frames');
        var build = function () {
          spec.split('~').forEach(function (pair) {
            var u = pair.trim().split('|'); if (!u[0]) return;
            var im = document.createElement('img');
            im.decoding = 'async'; im.loading = 'lazy'; im.alt = ''; im.width = 700; im.height = 875;
            im.sizes = '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 92vw';
            im.srcset = u[0] + ' 500w, ' + (u[1] || u[0]) + ' 700w';
            im.src = u[0];
            stage.appendChild(im);
          });
          start();
        };
        /* Built when the tile is within 200px of the viewport, not on load.
           Every tile is below the fold, so on a phone this keeps roughly two
           megabytes of decorative frames out of the initial load entirely and
           the hero gets the bandwidth. Without IntersectionObserver, fall back
           to building after load. */
        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries, obs) {
            if (!entries.some(function (e) { return e.isIntersecting; })) return;
            obs.disconnect(); idle(build);
          }, { rootMargin: '200px 0px' });
          io.observe(tile);
        } else if (document.readyState === 'complete') idle(build);
        else on(window, 'load', function () { idle(build); });
      }
      var imgs = stage ? $$('img', stage) : [];
      function start() {
        imgs = stage ? $$('img', stage) : [];
        if (imgs.length < 2 || reduce || tile.__cycling) return;
        tile.__cycling = true;
        var active = 0;
        setInterval(function () { if (tile.classList.contains('is-offscreen')) return; imgs[active].classList.remove('is-active'); active = (active + 1) % imgs.length; imgs[active].classList.add('is-active'); }, 1300);
      }
      start();
      if (reduce || !window.matchMedia('(hover: hover)').matches) return;
      on(tile, 'mousemove', function (e) {
        var r = tile.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        tile.style.transform = 'rotateX(' + (-y * 8).toFixed(2) + 'deg) rotateY(' + (x * 12).toFixed(2) + 'deg) translateY(-6px)';
        if (stage) stage.style.transform = 'translate3d(' + (-x * 22).toFixed(2) + 'px,' + (-y * 14).toFixed(2) + 'px,40px) scale(1.06)';
        if (label) label.style.transform = 'translate3d(' + (-x * 8).toFixed(2) + 'px,' + (-y * 4).toFixed(2) + 'px,22px)';
      });
      on(tile, 'mouseleave', function () { tile.style.transform = ''; if (stage) stage.style.transform = ''; if (label) label.style.transform = ''; });
    });
  }


  /* ── Off-screen pause ─────────────────────────────────────────────
     The snowfall, marquee, play ring, lantern and the tiles' cross-fade
     all run forever in Hydrogen, on screen or not. On a phone every one of
     them is a compositor commit per frame, so they only run while the
     element is within 120px of the viewport. */
  function offscreenPause() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.target.classList.toggle('is-offscreen', !e.isIntersecting); });
    }, { rootMargin: '120px 0px' });
    $$('[data-tile], .marquee, [data-night-hero], .film, .tape-wrap, .cabin-badge, .media-band').forEach(function (el) { io.observe(el); });
  }

  /* ── Night hero ───────────────────────────────────────────────── */
  function nightHero() {
    var sec = $('[data-night-hero]'); if (!sec) return;
    var src = sec.getAttribute('data-video'); if (!src) return;
    if (reduce || window.matchMedia('(max-width: 640px)').matches) return;
    var v = document.createElement('video');
    v.src = src; v.muted = true; v.loop = true; v.autoplay = true; v.playsInline = true;
    v.setAttribute('playsinline', ''); v.setAttribute('muted', ''); v.preload = 'auto'; v.setAttribute('aria-hidden', 'true');
    var media = $('[data-night-media]', sec); if (media) media.appendChild(v);
    var p = v.play(); if (p && p.catch) p.catch(function () {});
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { var q = v.play(); if (q && q.catch) q.catch(function () {}); } else v.pause(); });
    }, { threshold: 0.1 }).observe(sec);
  }

  /* ── Click-to-play film + YouTube cards ───────────────────────── */
  function film() {
    $$('[data-film]').forEach(function (wrap) {
      var btn = $('[data-film-play]', wrap), v = $('video', wrap); if (!btn || !v) return;
      on(btn, 'click', function () {
        v.play().then(function () { btn.hidden = true; v.controls = true; }).catch(function () {
          v.muted = true; v.play().then(function () { btn.hidden = true; v.controls = true; });
        });
      });
    });
    // Deferred YouTube embeds: the iframe only exists once someone presses play.
    $$('[data-video]').forEach(function (card) {
      var btn = $('[data-video-play]', card); if (!btn) return;
      on(btn, 'click', function () {
        var id = card.getAttribute('data-video');
        var f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        f.title = btn.getAttribute('aria-label') || 'Video';
        f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        f.allowFullscreen = true;
        btn.replaceWith(f);
      });
    });
  }

  /* ── Overlays: search / subscribe / cart ──────────────────────────
     One open at a time. Esc, backdrop and any [data-ov-close] close it;
     the body scroll locks while open. `data-open="x"` on any element
     opens one, and so does `pook:x-open` on window. */
  var overlays = {};
  var current = null;
  function openOverlay(name) {
    var ov = overlays[name]; if (!ov) return;
    if (name === 'cart' && window.pookCartSnapshot) {
      var c = window.pookCartSnapshot();
      if (c && c.item_count) track('view_cart', { currency: c.currency, value: c.total_price / 100, items: c.items.map(lineToItem) });
    }
    if (current && current !== name) closeOverlay();
    /* Closed overlays are display:none, not just invisible: a hidden sheet still
       carried a full-viewport backdrop-filter and a blended knit texture through
       every scroll frame — measured as most of the phone's dropped frames. */
    clearTimeout(ov.hideTimer); ov.el.hidden = false; void ov.el.offsetWidth;
    ov.el.classList.add('is-open'); ov.el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lock');
    current = name;
    if (ov.onOpen) ov.onOpen();
  }
  function closeOverlay() {
    if (!current) return;
    var ov = overlays[current];
    ov.el.classList.remove('is-open'); ov.el.setAttribute('aria-hidden', 'true');
    ov.hideTimer = setTimeout(function () { if (!ov.el.classList.contains('is-open')) ov.el.hidden = true; }, 450);
    document.documentElement.classList.remove('lock');
    if (ov.onClose) ov.onClose();
    current = null;
  }
  function overlayCore() {
    $$('[data-ov]').forEach(function (el) {
      var name = el.getAttribute('data-ov');
      overlays[name] = { el: el };
      $$('[data-ov-close]', el).forEach(function (b) { on(b, 'click', closeOverlay); });
      window.addEventListener('pook:' + name + '-open', function () { openOverlay(name); });
    });
    on(document, 'click', function (e) {
      var t = e.target.closest('[data-open]'); if (!t) return;
      e.preventDefault(); openOverlay(t.getAttribute('data-open'));
    });
    on(window, 'keydown', function (e) { if (e.key === 'Escape') closeOverlay(); });
  }

  /* ── Search sheet ─────────────────────────────────────────────── */
  function search() {
    var ov = overlays.search; if (!ov) return;
    var el = ov.el, input = $('[data-search-input]', el), form = $('[data-search-form]', el);
    var clear = $('[data-search-clear]', el), sugg = $('[data-search-sugg]', el);
    var results = $('[data-search-results]', el), count = $('[data-search-count]', el);
    var empty = $('[data-search-empty]', el), list = $('[data-search-list]', el);
    var PH = ['search toques…', 'search dukes…', 'search onesies…', 'search the cabin closet…', 'search pookies…'];
    var phIdx = 0, phChars = 0, phTimer = null, debounce = null, hits = [];

    function typewriter() {
      clearTimeout(phTimer);
      if (current !== 'search' || input.value) return;
      var target = PH[phIdx];
      if (phChars < target.length) { phChars++; input.placeholder = target.slice(0, phChars) + '▍'; phTimer = setTimeout(typewriter, 55); }
      else { input.placeholder = target; phTimer = setTimeout(function () { phIdx = (phIdx + 1) % PH.length; phChars = 0; typewriter(); }, 1700); }
    }
    function render(q) {
      var has = q.trim().length > 0;
      sugg.hidden = has; results.hidden = !has; clear.hidden = !has;
      if (!has) { list.innerHTML = ''; return; }
      count.textContent = hits.length === 0 ? 'Nothing matched' : hits.length + ' result' + (hits.length === 1 ? '' : 's');
      empty.hidden = hits.length !== 0;
      list.innerHTML = hits.map(function (p) {
        var img = p.featured_image ? p.featured_image.url : (p.image || '');
        if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=400';
        var cat = (p.type || '').toLowerCase();
        return '<li><a class="srch__hit" href="' + esc(p.url) + '">' +
          '<img src="' + esc(img) + '" alt="" width="56" height="56" loading="lazy">' +
          '<span style="flex:1;min-width:0"><span class="srch__hit-t" style="display:block">' + esc(p.title) + '</span><span class="srch__hit-c" style="display:block">' + esc(cat) + '</span></span>' +
          (p.price ? '<span class="srch__hit-p">$' + Math.round(parseFloat(p.price)) + '</span>' : '') + '</a></li>';
      }).join('');
    }
    function query() {
      var q = input.value.trim();
      if (!q) { hits = []; render(''); return; }
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last')
        .then(function (r) { return r.json(); })
        .then(function (d) {
        track('search', { search_term: q, results: ((d.resources && d.resources.results && d.resources.results.products) || []).length }); hits = (d.resources && d.resources.results && d.resources.results.products) || []; render(q); })
        .catch(function () { hits = []; render(q); });
    }
    ov.onOpen = function () { setTimeout(function () { input.focus(); }, 50); phChars = 0; typewriter(); };
    ov.onClose = function () { input.value = ''; input.placeholder = ''; hits = []; render(''); clearTimeout(phTimer); };
    on(input, 'input', function () { clearTimeout(debounce); if (input.value) { input.placeholder = ''; clearTimeout(phTimer); } else typewriter(); debounce = setTimeout(query, 220); });
    on(clear, 'click', function () { input.value = ''; hits = []; render(''); input.focus(); typewriter(); });
    $$('[data-search-pill]', el).forEach(function (b) { on(b, 'click', function () { input.value = b.textContent.trim(); input.placeholder = ''; clearTimeout(phTimer); query(); }); });
    // Enter opens the full results page for what was typed (Joe, 23 Oct: "hats" + Enter
    // jumped to whichever product came first). The form posts to /search?q=… on its own.
  }

  /* ── Subscribe sheet + inline forms ─────────────────────────────
     Every subscribe form is the platform customer form, posted via
     fetch so the sheet and the footer strip can show their done state
     without leaving the page. */
  function subscribe() {
    var ov = overlays.subscribe;
    $$('[data-sub-form]').forEach(function (form) {
      var btn = $('[data-sub-submit]', form), err = $('[data-sub-error]', form);
      on(form, 'submit', function (e) {
        e.preventDefault();
        var email = $('[data-sub-email]', form).value.trim(); if (!email) return;
        btn.disabled = true; var was = btn.textContent; btn.textContent = 'Sending…';
        fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) {
            btn.disabled = false;
            if (!r.ok) throw new Error('bad');
            track('sign_up', { method: form.hasAttribute('data-sub-inline') ? 'footer' : 'sheet' });
            if (form.hasAttribute('data-sub-inline')) { form.classList.add('is-done'); $('[data-sub-email]', form).disabled = true; btn.textContent = 'Welcome ✓'; btn.disabled = true; return; }
            var prompt = $('[data-sub-prompt]', ov.el), done = $('[data-sub-done]', ov.el);
            if (prompt) prompt.hidden = true; if (done) done.hidden = false;
          })
          .catch(function () { btn.disabled = false; btn.textContent = was; if (err) { err.textContent = 'Something went wrong — please try again.'; err.hidden = false; } });
      });
    });
    if (ov) {
      ov.onOpen = function () { var p = $('[data-sub-prompt]', ov.el), d = $('[data-sub-done]', ov.el); if (p) p.hidden = false; if (d) d.hidden = true; setTimeout(function () { var i = $('[data-sub-email]', ov.el); if (i) i.focus(); }, 220); };
    }
    // Exit intent: once per session, desktop only, armed after 9s.
    try { if (sessionStorage.getItem('pook:exitintent')) return; } catch (e) {}
    if (!window.matchMedia('(hover: hover)').matches) return;
    var armed = false; setTimeout(function () { armed = true; }, 9000);
    var onLeave = function (e) {
      if (!armed || e.clientY > 0 || e.relatedTarget || current) return;
      try { sessionStorage.setItem('pook:exitintent', '1'); } catch (x) {}
      document.removeEventListener('mouseout', onLeave);
      openOverlay('subscribe');
    };
    document.addEventListener('mouseout', onLeave);
  }


  /* ── Reviews: toggle + submit through Shopify's contact form ───── */
  function reviews() {
    $$('[data-reviews]').forEach(function (root) {
      var toggle = $('[data-review-toggle]', root), wrap = $('[data-review-wrap]', root); if (!toggle || !wrap) return;
      var form = $('form', wrap), done = $('[data-review-done]', wrap), err = $('[data-review-error]', wrap), btn = $('[data-review-submit]', wrap);
      var setOpen = function (open) {
        wrap.hidden = !open; toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.textContent = toggle.getAttribute(open ? 'data-close-label' : 'data-open-label');
      };
      on(toggle, 'click', function () { var open = wrap.hidden; setOpen(open); if (open && form) { var first = $('input[type="radio"]', form); if (first) first.focus(); } });
      if (!form) return;
      /* Back from Shopify's bot check (a native submit): show the thank-you state the fetch path would have shown. */
      if (location.search.indexOf('contact_posted=true') > -1) { setOpen(true); form.hidden = true; done.hidden = false; try { history.replaceState(null, '', location.pathname); } catch (x) {} }
      on(form, 'submit', function (e) {
        e.preventDefault();
        if (!$('input[name="contact[rating]"]:checked', form)) { err.textContent = 'Pick a star rating first.'; err.hidden = false; return; }
        err.hidden = true; btn.disabled = true; var was = btn.textContent; btn.textContent = 'Sending…';
        fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
          .then(function (r) {
            if (r.url.indexOf('/challenge') > -1) { form.submit(); return; }           /* bot check: let Shopify show it */
            if (!r.ok || r.url.indexOf('contact_posted=true') < 0) throw new Error('bad');
            form.hidden = true; done.hidden = false;
            setTimeout(function () { setOpen(false); form.reset(); form.hidden = false; done.hidden = true; }, 4000);
          })
          .catch(function () { err.textContent = "Couldn't save your review — please try again."; err.hidden = false; })
          .then(function () { btn.disabled = false; btn.textContent = was; });
      });
    });
  }

  /* ── Shipping progress bar with every tier ───────────────────────
     Joe, 21 Oct: show each shipping step on the bar, not just free shipping —
     "if someone orders around $52 but can get to 60 the shipping drops $10".
     Tiers come from Theme settings → Cart (snippets/ship-tiers), as
     "fromCents:priceCents,…". The bar runs to the free-shipping step; each
     step is a marker that lights up once the subtotal reaches it. */
  function parseTiers(str) {
    return String(str || '').split(',').map(function (p) { var a = p.split(':'); return { from: parseInt(a[0], 10), price: parseInt(a[1], 10) }; })
      .filter(function (t) { return !isNaN(t.from) && !isNaN(t.price); }).sort(function (a, b) { return a.from - b.from; });
  }
  function shipBar(note, fill, steps, sub, tiers, freeMinFallback) {
    if (!tiers.length) tiers = [{ from: 0, price: 1 }, { from: freeMinFallback || 9901, price: 0 }];
    var top = tiers[tiers.length - 1].from || 1, cur = tiers[0], next = null;
    tiers.forEach(function (t) { if (sub >= t.from) cur = t; else if (!next) next = t; });
    var label = function (t) { return t.price === 0 ? 'free shipping' : money(t.price) + ' shipping'; };
    var txt;
    if (!next) txt = cur.price === 0 ? "🎉 You've unlocked free shipping" : 'Shipping ' + money(cur.price);
    else {
      txt = cur.from > 0 && cur.price > 0 && next.price === 0
        ? money(cur.price) + ' shipping unlocked · ' + money(next.from - sub) + ' from free'
        : "You're " + money(next.from - sub) + ' from ' + label(next);
      var free = tiers.filter(function (t) { return t.price === 0 && t.from > next.from; })[0];
      if (free) txt += ' · ' + money(free.from - sub) + ' from free';
    }
    if (note) note.textContent = txt;
    if (fill) fill.style.width = Math.min(100, Math.round(sub / top * 1000) / 10) + '%';
    if (steps) steps.innerHTML = tiers.filter(function (t) { return t.from > 0; }).map(function (t) {
      var at = Math.min(100, t.from / top * 100), hit = sub >= t.from, dollars = Math.round(t.from / 100);
      return '<span class="ship-step' + (hit ? ' is-hit' : '') + (at >= 99.5 ? ' is-end' : '') + '" style="left:' + at + '%">' +
        '<i aria-hidden="true"></i><b>$' + dollars + '</b><em>' + (t.price === 0 ? 'Free' : '$' + (t.price / 100).toFixed(2)) + '</em></span>';
    }).join('');
  }
  function shipBars() {
    $$('[data-ship-bar]').forEach(function (b) {
      shipBar($('[data-ship-note]', b), $('[data-ship-fill]', b), $('[data-ship-steps]', b), parseInt(b.getAttribute('data-subtotal') || '0', 10), parseTiers(b.getAttribute('data-ship-tiers')));
    });
  }

  /* ── Cart drawer (Cart AJAX API) ──────────────────────────────── */
  function cartDrawer() {
    var ov = overlays.cart; if (!ov) return;
    var el = ov.el, lines = $('[data-cart-lines]', el), emptyEl = $('[data-cart-empty]', el), foot = $('[data-cart-foot]', el);
    var upsell = $('[data-cart-upsell]', el), upsellList = $('[data-cart-upsell-list]', el);
    var freeMin = parseInt(el.getAttribute('data-free-min-cents') || '9901', 10);
    var cartData = null;

    function paint(c) {
      cartData = c;
      var n = c.item_count;
      $$('[data-cart-count]').forEach(function (b) {
        b.textContent = n > 99 ? '99+' : n; b.hidden = n === 0;
        var a = b.closest('a'); if (a) a.setAttribute('aria-label', 'Cart' + (n ? ', ' + n + ' item' + (n === 1 ? '' : 's') : ''));
      });
      var lbl = $('[data-cart-count-label]', el); if (lbl) lbl.textContent = n + ' item' + (n === 1 ? '' : 's');
      emptyEl.hidden = n > 0; foot.hidden = n === 0;
      lines.innerHTML = c.items.map(function (it) {
        var v = it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '';
        return '<li class="dline" data-key="' + esc(it.key) + '">' +
          '<a class="dline__img" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(it.image + (it.image.indexOf('?') > -1 ? '&' : '?') + 'width=200') + '" alt="" width="96" height="96">' : '') + '</a>' +
          '<div class="dline__mid"><div><a class="dline__t" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' + (v ? '<div class="dline__v">' + esc(v) + '</div>' : '') + '</div>' +
          '<div class="dline__row"><div class="qty"><button type="button" data-qty="-1" aria-label="Decrease"' + (it.quantity <= 1 ? ' disabled' : '') + '>−</button><output>' + it.quantity + '</output><button type="button" data-qty="1" aria-label="Increase">+</button></div>' +
          '<div class="dline__p">' + money(it.final_line_price) + '</div></div></div>' +
          '<button type="button" class="dline__rm" data-remove aria-label="Remove">Remove</button></li>';
      }).join('');
      /* Automatic and code discounts (e.g. a sitewide sale) come off the
         cart as a whole: list each one and show the total after them, as the
         live cart does. Shipping tiers are judged on that total too. */
      var disc = $('[data-cart-discounts]', el), apps = c.cart_level_discount_applications || [];
      if (disc) {
        disc.innerHTML = apps.map(function (d) { return '<div class="drawer__disc-row"><span>' + esc(d.title || 'Discount') + '</span><span>−' + money(d.total_allocated_amount) + '</span></div>'; }).join('');
        disc.hidden = !apps.length;
      }
      var sub = c.total_price;
      $('[data-cart-subtotal]', el).textContent = money(sub);
      shipBar($('[data-cart-ship-note]', el), $('[data-cart-ship-fill]', el), $('[data-cart-ship-steps]', el), sub, parseTiers(el.getAttribute('data-ship-tiers')), freeMin);
      recs(c);
    }
    window.pookCartSnapshot = function () { return cartData; };
    function refresh() { return fetch('/cart.js').then(function (r) { return r.json(); }).then(paint); }
    function change(key, qty) {
      var before = cartData && cartData.items.filter(function (i) { return i.key === key; })[0];
      return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) }).then(function (r) { return r.json(); }).then(function (c) {
        if (before) {
          var was = before.quantity, delta = was - qty;
          if (delta > 0) { var it = lineToItem(before); it.quantity = delta; track('remove_from_cart', { currency: c.currency, value: (before.final_price * delta) / 100, items: [it] }); }
          else if (delta < 0) { var ai = lineToItem(before); ai.quantity = -delta; track('add_to_cart', { currency: c.currency, value: (before.final_price * -delta) / 100, items: [ai] }); }
        }
        return paint(c);
      });
    }
    function add(id, qty) {
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: id, quantity: qty || 1 }] }) })
        .then(function (r) { if (!r.ok) throw r; return r.json(); })
        .then(function (added) {
          var items = (added && added.items) || [];
          if (items.length) track('add_to_cart', { currency: (cartData && cartData.currency) || currencyCode(), value: items.reduce(function (a, i) { return a + i.final_line_price / 100; }, 0), items: items.map(lineToItem) });
          if (window.pookWriteAttribution) window.pookWriteAttribution();
          return refresh();
        });
    }
    function recs(c) {
      if (!c.items.length) { upsell.hidden = true; return; }
      var inCart = {}; c.items.forEach(function (i) { inCart[i.handle] = 1; });
      fetch('/recommendations/products.json?product_id=' + c.items[0].product_id + '&limit=6')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var picks = (d.products || []).filter(function (p) { return p.available && !inCart[p.handle] && p.variants && p.variants.length; }).slice(0, 2);
          if (!picks.length) { upsell.hidden = true; return; }
          upsellList.innerHTML = picks.map(function (p) {
            return '<li>' + (p.featured_image ? '<img src="' + esc(p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=120') + '" alt="" width="48" height="48">' : '') +
              '<div style="flex:1;min-width:0"><div class="upsell__t">' + esc(p.title) + '</div><div class="upsell__p">' + money(p.price) + '</div></div>' +
              '<button type="button" class="upsell__add" data-upsell-add="' + p.variants[0].id + '">Add</button></li>';
          }).join('');
          upsell.hidden = false;
        }).catch(function () { upsell.hidden = true; });
    }
    on(el, 'click', function (e) {
      var q = e.target.closest('[data-qty]');
      if (q) { var li = q.closest('[data-key]'), out = $('output', li); return change(li.getAttribute('data-key'), parseInt(out.textContent, 10) + parseInt(q.getAttribute('data-qty'), 10)); }
      var rm = e.target.closest('[data-remove]');
      if (rm) return change(rm.closest('[data-key]').getAttribute('data-key'), 0);
      var up = e.target.closest('[data-upsell-add]');
      if (up) { up.disabled = true; up.textContent = '…'; return add(up.getAttribute('data-upsell-add')); }
      if (e.target.closest('[data-cart-clear]')) return fetch('/cart/clear.js', { method: 'POST' }).then(refresh);
    });
    ov.onOpen = function () { refresh(); };
    window.pookCartAdd = function (id, qty) { return add(id, qty).then(function () { openOverlay('cart'); }); };
    window.pookRefreshCart = refresh;
  }

  /* ── PDP ──────────────────────────────────────────────────────── */
  function pdp() {
    var root = $('[data-pdp]'); if (!root) return;
    var main = $('[data-pdp-main]', root);
    /* Swap the photo only once the new one has loaded, so the frame never goes blank. */
    function swapTo(url) {
      if (!url) return;
      var pre = new Image();
      pre.onload = pre.onerror = function () { if (main.getAttribute('data-want') !== url) return; main.removeAttribute('srcset'); main.src = url; };
      main.setAttribute('data-want', url); pre.src = url;
    }
    /* Product photos that run off their own edges (a close-up, or a shot cropped at the
       top) look "cut off" floating inside the white frame — Joe, 20 Oct, Yeti Trapper Hat.
       Read the photo's edges once it loads: cut on one side → pin it to that side of the
       frame; cut on two or more → fill the frame. Whole-product photos are untouched. */
    function edgeFit() {
      if (!main || main.closest('.pdp__main--fill')) return;
      try {
        var n = 24, c = document.createElement('canvas'); c.width = c.height = n;
        var x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(main, 0, 0, n, n);
        var d = x.getImageData(0, 0, n, n).data;
        function solid(i) { var r = d[i], g = d[i + 1], b = d[i + 2], al = d[i + 3]; return al > 200 && !(r > 238 && g > 238 && b > 238); }
        function side(fn) { var k = 0; for (var j = 0; j < n; j++) if (solid(fn(j))) k++; return k / n > 0.25; }
        var t = side(function (j) { return j * 4; }), btm = side(function (j) { return ((n - 1) * n + j) * 4; }),
            l = side(function (j) { return (j * n) * 4; }), r = side(function (j) { return (j * n + n - 1) * 4; });
        var cnt = (t ? 1 : 0) + (btm ? 1 : 0) + (l ? 1 : 0) + (r ? 1 : 0);
        main.removeAttribute('data-bleed'); main.style.objectPosition = '';
        if (!cnt) return;
        /* Filling crops, so only when the photo is already close to the 4:5 frame; a square
           graphic (gift card, flat-lay) keeps every edge and is just pinned to its cut sides. */
        var ratio = main.naturalWidth / main.naturalHeight;
        if (cnt >= 2 && Math.abs(ratio - 0.8) <= 0.08) { main.setAttribute('data-bleed', 'fill'); return; }
        main.setAttribute('data-bleed', 'pin');
        main.style.objectPosition = (l && !r ? 'left' : r && !l ? 'right' : 'center') + ' ' + (t && !btm ? 'top' : btm && !t ? 'bottom' : 'center');
      } catch (e) { /* cross-origin or not decoded yet: leave as is */ }
    }
    if (main) { on(main, 'load', edgeFit); if (main.complete && main.naturalWidth) edgeFit(); }

    /* Swipe the main photo to move between photos — Joe, 20 Oct: "my instinct is to swipe
       to the left to see the additional images". A tap still opens the full-screen viewer. */
    var frameEl = $('[data-pdp-frame]', root), sx = null, sy = null, swiped = false;
    function step(dir) {
      var ts = $$('[data-thumb]', root); if (ts.length < 2) return;
      var cur = ts.map(function (b) { return b.getAttribute('aria-pressed') === 'true'; }).indexOf(true);
      var nx = ts[((cur < 0 ? 0 : cur) + dir + ts.length) % ts.length];
      nx.click(); if (nx.scrollIntoView) nx.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
    if (frameEl) {
      on(frameEl, 'touchstart', function (e) { if (e.touches.length !== 1) { sx = null; return; } sx = e.touches[0].clientX; sy = e.touches[0].clientY; swiped = false; }, { passive: true });
      on(frameEl, 'touchend', function (e) {
        if (sx === null) return;
        var dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy; sx = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) { swiped = true; step(dx < 0 ? 1 : -1); }
      });
      /* A swipe ends in a click on some browsers — swallow that one so it doesn't open the viewer. */
      frameEl.addEventListener('click', function (e) { if (swiped) { swiped = false; e.stopImmediatePropagation(); e.preventDefault(); } }, true);
    }

    /* Warm the large photos after the page settles, so thumbnail clicks are instant. */
    setTimeout(function () { $$('[data-thumb]', root).slice(0, 8).forEach(function (b) { var i = new Image(); i.src = b.getAttribute('data-full'); }); }, 2500);
    $$('[data-thumb]', root).forEach(function (b) {
      on(b, 'click', function () { $$('[data-thumb]', root).forEach(function (o) { o.setAttribute('aria-pressed', 'false'); }); b.setAttribute('aria-pressed', 'true'); if (main) swapTo(b.getAttribute('data-full')); });
    });
    var data = $('[data-variants]', root), variants = [];
    if (data) { try { variants = JSON.parse(data.textContent); } catch (e) {} }
    var idField = $('[data-variant-id]', root), priceEl = $('[data-price]', root), compareEl = $('[data-compare]', root);
    var addBtn = $('[data-add]', root), barPrice = $('[data-bar-price]'), form = $('[data-pdp-form]', root);
    function selected() { return $$('[data-optgroup]', root).map(function (g) { var b = $('[aria-pressed="true"]', g); return b ? b.getAttribute('data-value') : null; }); }
    function apply() {
      var want = selected(), match = null;
      for (var i = 0; i < variants.length && !match; i++) {
        var ok = true; for (var j = 0; j < want.length; j++) if (want[j] !== null && variants[i].options[j] !== want[j]) { ok = false; break; }
        if (ok) match = variants[i];
      }
      if (!match) return;
      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = match.price;
      if (barPrice) barPrice.textContent = match.price;
      if (compareEl) { compareEl.textContent = match.compare || ''; compareEl.hidden = !match.compare; }
      if (main && match.image) swapTo(match.image);
      var sp = $('[data-shop-pay-el]'); if (sp) sp.setAttribute('variants', match.id + ':1');
      var spw = $('[data-shop-pay]'); if (spw) spw.hidden = !match.available;
      $$('[data-add],[data-bar-add]').forEach(function (b) { b.disabled = !match.available; b.textContent = match.available ? 'Add to cart' : 'Sold out'; });
      if (history.replaceState) { var u = new URL(location.href); u.searchParams.set('variant', match.id); history.replaceState({}, '', u); }
    }
    $$('[data-optgroup]', root).forEach(function (group) {
      $$('button', group).forEach(function (b) { on(b, 'click', function () { $$('button', group).forEach(function (o) { o.setAttribute('aria-pressed', 'false'); }); b.setAttribute('aria-pressed', 'true'); apply(); }); });
    });
    // Add to cart opens the drawer instead of leaving the page.
    on(form, 'submit', function (e) {
      if (!window.pookCartAdd) return;
      e.preventDefault();
      var was = addBtn.textContent; addBtn.disabled = true; addBtn.textContent = 'Adding…';
      window.pookCartAdd(idField.value, 1).then(function () { addBtn.disabled = false; addBtn.textContent = was; })
        .catch(function () { addBtn.disabled = false; addBtn.textContent = was; form.submit(); });
    });
    var bar = $('[data-buy-bar]');
    if (bar) {
      on(window, 'scroll', function () { bar.classList.toggle('is-in', window.scrollY > 560); }, { passive: true });
      on($('[data-bar-add]', bar), 'click', function () { form.requestSubmit ? form.requestSubmit() : form.submit(); });
    }
  }

  /* ── Recommendations — Shopify renders the section, we swap it in ── */
  function recommendations() {
    var el = $('[data-recommendations]'); if (!el || !el.getAttribute('data-url')) return;
    fetch(el.getAttribute('data-url')).then(function (r) { return r.text(); }).then(function (html) {
      var next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
      if (next && next.innerHTML.trim()) { el.innerHTML = next.innerHTML; if (window.pookArmFade) window.pookArmFade(el); }
    }).catch(function () {});
  }

  /* ── Recently viewed ────────────────────────────────────────────
     Same localStorage key and record shape as app/lib/recently-viewed.ts,
     so the ring is shared with the Hydrogen build. */
  function recentlyViewed() {
    var KEY = 'pook:recently-viewed', MAX = 12, cur = document.body.getAttribute('data-product-handle'), list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); if (!Array.isArray(list)) list = []; } catch (e) {}
    if (cur) {
      var here = $('[data-pdp]');
      var rec = { handle: cur, category: here ? here.getAttribute('data-part') || '' : '', title: (here && $('.pdp__title', here)) ? $('.pdp__title', here).textContent.trim() : document.title,
        image: (here && $('[data-pdp-main]', here)) ? $('[data-pdp-main]', here).src : undefined,
        amount: (here && $('[data-price]', here)) ? $('[data-price]', here).textContent.replace(/[^0-9.]/g, '') : undefined };
      list = list.filter(function (x) { return x && x.handle !== cur; }); list.unshift(rec); list = list.slice(0, MAX);
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }
    var host = $('[data-recent]'); if (!host) return;
    var rail = $('[data-recent-rail]', host), show = list.filter(function (x) { return x && x.handle !== cur; }).slice(0, 6);
    if (!show.length || !rail) return;
    rail.innerHTML = show.map(function (p) {
      var img = p.image ? p.image.replace(/([?&])width=\d+/, '$1width=400') : '';
      return '<a class="pcard pcard--cat" href="/products/' + esc(p.handle) + '"><div class="pcard__media">' + (img ? '<img class="fade-img" data-fade src="' + esc(img) + '" alt="" loading="lazy" width="400" height="500">' : '') + '</div>' +
        '<div class="pcard__body"><div class="pcard__title">' + esc(p.title) + '</div>' + (p.amount ? '<div class="pcard__price">$' + Math.round(parseFloat(p.amount)) + '</div>' : '') + '</div></a>';
    }).join('');
    host.hidden = false; if (window.pookArmFade) window.pookArmFade(rail);
  }

  /* ── Policy pages ───────────────────────────────────────────────
     Shopify renders fixed .shopify-policy__* markup and ignores any
     theme template, so the page-hero chrome is added here instead. */
  function policyChrome() {
    var title = $('.shopify-policy__title'), body = $('.shopify-policy__body'); if (!title || !body) return;
    var h1 = $('h1', title); var word = h1 ? h1.textContent.trim().split(' ')[0] : '';
    var eyebrow = document.createElement('div'); eyebrow.className = 'band__eyebrow'; eyebrow.textContent = 'Fine print';
    var ghost = document.createElement('div'); ghost.className = 'band__ghost'; ghost.setAttribute('aria-hidden', 'true'); ghost.textContent = word;
    var knit = document.createElement('div'); knit.className = 'band__knit'; knit.setAttribute('aria-hidden', 'true');
    title.insertBefore(eyebrow, h1); title.appendChild(ghost); title.insertBefore(knit, title.firstChild);
    var back = document.createElement('a'); back.className = 'policy-back'; back.href = '/policies'; back.textContent = '← All policies';
    body.insertBefore(back, body.firstChild);
  }

  // Every block runs in isolation: a failure in one must not stop the
  // others (HockeySockey lost its scroll chrome to a broken reveal once).

  /* ── 360 turntable ──────────────────────────────────────────────
     Frames come from `custom.spin_frames`, already in angle order.

     Drawn to a canvas, not swapped into an <img>: setting .src costs a
     decode per frame and that is what made dragging stutter. Every frame
     is decoded once up front, then a drag is just a drawImage.

     Nothing is fetched until the viewer is opened — 36 frames is a lot to
     spend on someone who never drags. Horizontal intent is required before
     we take the gesture, so a vertical swipe still scrolls the page. */
  function spin() {
    var btn = $('[data-spin-open]'); if (!btn) return;
    var wrap = btn.closest('.pdp__main'), cv = $('[data-spin-canvas]', wrap);
    if (!wrap || !cv || !cv.getContext) return;
    var frames = [];
    try { frames = JSON.parse(btn.getAttribute('data-frames') || '[]'); } catch (e) { return; }
    if (frames.length < 2) return;

    var ctx = cv.getContext('2d'), imgs = new Array(frames.length);
    var at = 0, open = false, ready = false, pending = null;
    var label = $('[data-spin-label]', btn);
    var url = function (u, w) { return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; };

    function size() {
      var r = wrap.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = Math.round(r.width * dpr); cv.height = Math.round(r.height * dpr);
      cv.style.width = r.width + 'px'; cv.style.height = r.height + 'px';
    }
    function draw() {
      var im = imgs[at];
      if (!im || !im.complete || !im.naturalWidth) return;
      ctx.clearRect(0, 0, cv.width, cv.height);
      /* contain, with the same 7% breathing room the still image has */
      var pad = 0.07, bw = cv.width * (1 - pad * 2), bh = cv.height * (1 - pad * 2);
      var s = Math.min(bw / im.naturalWidth, bh / im.naturalHeight);
      var w = im.naturalWidth * s, h = im.naturalHeight * s;
      ctx.drawImage(im, (cv.width - w) / 2, (cv.height - h) / 2, w, h);
    }
    function show(i) {
      at = ((i % frames.length) + frames.length) % frames.length;
      if (pending) return;
      pending = requestAnimationFrame(function () { pending = null; draw(); });
    }
    function load(done) {
      if (ready) { done(); return; }
      var left = frames.length;
      frames.forEach(function (f, i) {
        var im = new Image();
        im.decoding = 'async';
        im.onload = im.onerror = function () { if (--left === 0) { ready = true; done(); } };
        im.src = url(f, 900);
        imgs[i] = im;
      });
    }

    function openSpin(to) {
      if (open) { if (to != null) show(to); return; }
      open = true;
      if (label) label.textContent = 'Loading…';
      load(function () {
        if (label) label.textContent = 'Drag to spin';
        size(); cv.hidden = false; wrap.classList.add('is-spinning'); show(to == null ? 0 : to);
      });
    }
    on(btn, 'click', function (e) { e.preventDefault(); openSpin(null); });

    /* Thumbnails that are angles rather than photographs. */
    $$('[data-spin-to]').forEach(function (b) {
      on(b, 'click', function () {
        $$('[data-spin-to], [data-thumb]').forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        openSpin(parseInt(b.getAttribute('data-spin-to'), 10) || 0);
      });
    });
    /* A real photograph closes the spin so the still image is visible again. */
    $$('[data-thumb]').forEach(function (b) {
      on(b, 'click', function () {
        $$('[data-spin-to]').forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
        if (open) { cv.hidden = true; wrap.classList.remove('is-spinning'); open = false; }
      });
    });

    var STEP = 8, downX = null, downY = null, base = 0, decided = false;
    function start(x, y) { downX = x; downY = y; base = at; decided = false; cv.classList.add('is-grabbing'); }
    function move(x, y, ev) {
      if (downX === null || !open) return;
      var dx = x - downX, dy = y - downY;
      if (!decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        decided = true;
        if (Math.abs(dy) > Math.abs(dx)) { downX = null; cv.classList.remove('is-grabbing'); return; }
      }
      if (ev && ev.cancelable) ev.preventDefault();
      show(base + Math.round(dx / STEP));
    }
    function end() { downX = null; cv.classList.remove('is-grabbing'); }

    on(cv, 'pointerdown', function (e) { if (open) start(e.clientX, e.clientY); });
    on(window, 'pointermove', function (e) { move(e.clientX, e.clientY, e); });
    on(window, 'pointerup', end); on(window, 'pointercancel', end);
    on(cv, 'touchstart', function (e) { if (open && e.touches[0]) start(e.touches[0].clientX, e.touches[0].clientY); }, {passive: true});
    on(cv, 'touchmove', function (e) { if (e.touches[0]) move(e.touches[0].clientX, e.touches[0].clientY, e); }, {passive: false});
    on(cv, 'touchend', end);

    cv.tabIndex = 0;
    on(cv, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(at - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(at + 1); }
    });
    on(window, 'resize', function () { if (open) { size(); draw(); } });
  }

  function safe(name, fn) {
    try { fn(); } catch (err) { if (window.console && console.warn) console.warn('[theme] ' + name + ' failed', err); }
  }
  /* ── Full-screen product photos ───────────────────────────────
     Tony, 17 Oct: tap the product photo to see it full screen and zoom,
     like the Hockey Sockey site. Tap or double-click zooms in where you
     tap, pinch zooms on phones, drag pans, swipe or arrows move between
     photos, Esc or × closes. */
  /* Fans & Celebrity Sightings: filter chips, and each snapshot opens full
     screen with its caption; arrows and swipes step through the photos the
     current filter shows. */
  function fanGallery() {
    var wall = $('.fanpage') || $('[data-fan-wall]');
    if (!wall) return;
    var cards = $$('[data-fan-photo]', wall), chips = $$('[data-fan-filter]', wall);
    if (!cards.length) return;
    var box, img, cap, list = [], at = 0;
    chips.forEach(function (c) {
      on(c, 'click', function () {
        var g = c.getAttribute('data-fan-filter');
        chips.forEach(function (o) { var me = o === c; o.classList.toggle('is-on', me); o.setAttribute('aria-pressed', me ? 'true' : 'false'); });
        cards.forEach(function (k) { k.hidden = g !== 'all' && k.getAttribute('data-group') !== g; });
      });
    });
    function show(i) {
      at = (i + list.length) % list.length;
      var k = list[at], t = k.getAttribute('data-caption') || '';
      img.src = k.getAttribute('data-full'); img.alt = t;
      cap.textContent = t; cap.hidden = !t;
      $$('.pzoom__prev, .pzoom__next', box).forEach(function (b) { b.hidden = list.length < 2; });
    }
    function close() { box.hidden = true; document.documentElement.classList.remove('pzoom-open'); }
    function build() {
      box = document.createElement('div');
      box.className = 'pzoom pzoom--fans'; box.hidden = true;
      box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true'); box.setAttribute('aria-label', 'Fan and celebrity photos');
      box.innerHTML = '<img class="pzoom__img" alt="">' +
        '<button type="button" class="pzoom__btn pzoom__close" aria-label="Close">&times;</button>' +
        '<button type="button" class="pzoom__btn pzoom__prev" aria-label="Previous photo">&lsaquo;</button>' +
        '<button type="button" class="pzoom__btn pzoom__next" aria-label="Next photo">&rsaquo;</button>' +
        '<span class="pzoom__cap"></span>';
      document.body.appendChild(box);
      img = $('.pzoom__img', box); cap = $('.pzoom__cap', box);
      on($('.pzoom__close', box), 'click', close);
      on($('.pzoom__prev', box), 'click', function () { show(at - 1); });
      on($('.pzoom__next', box), 'click', function () { show(at + 1); });
      on(box, 'click', function (e) { if (e.target === box) close(); });
      var sx = null;
      on(box, 'touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
      on(box, 'touchend', function (e) { if (sx === null) return; var dx = e.changedTouches[0].clientX - sx; sx = null; if (Math.abs(dx) > 50) show(at + (dx < 0 ? 1 : -1)); });
      on(document, 'keydown', function (e) {
        if (box.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight') show(at + 1);
        else if (e.key === 'ArrowLeft') show(at - 1);
      });
    }
    cards.forEach(function (k) {
      on($('.snap__btn', k), 'click', function () {
        if (!box) build();
        list = cards.filter(function (c) { return !c.hidden; });
        show(list.indexOf(k)); box.hidden = false; document.documentElement.classList.add('pzoom-open');
        $('.pzoom__close', box).focus();
      });
    });
  }

  /* Homepage sale banner: put the usual banner back once the sale's end time
     passes, even on a cached page, and say "ends tonight" on the last day. */
  function saleHero() {
    var hero = $('[data-sale-ends]');
    if (!hero) return;
    var end = Date.parse(hero.getAttribute('data-sale-ends'));
    if (isNaN(end)) return;
    function check() {
      var ms = end - Date.now();
      if (ms <= 0) {
        var sale = $('[data-sale-copy]', hero), usual = $('[data-usual-copy]', hero), eyes = $('.night-hero__eyes', hero);
        if (sale) sale.remove(); if (eyes) eyes.remove(); if (usual) usual.hidden = false;
        hero.classList.remove('night-hero--sale');
        return;
      }
      var left = $('[data-sale-left]', hero);
      if (left && ms < 864e5) left.textContent = 'ends tonight';
      setTimeout(check, Math.min(ms + 50, 6e4));
    }
    check();
  }

  function photoZoom() {
    var frame = $('[data-pdp-frame]'), main = $('[data-pdp-main]');
    if (!frame || !main) return;
    var box, img, count, list = [], at = 0, s = 1, tx = 0, ty = 0;
    function big(u) { return String(u || '').replace(/([?&])width=\d+/, '$1width=2400'); }
    function collect() {
      list = $$('[data-thumb]').map(function (b) { return b.getAttribute('data-full'); }).filter(Boolean);
      if (!list.length) list = [main.currentSrc || main.src];
    }
    function paint() { img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + s + ')'; box.classList.toggle('is-zoomed', s > 1.01); }
    function reset() { s = 1; tx = 0; ty = 0; paint(); }
    function show(i) {
      at = (i + list.length) % list.length; reset();
      var want = big(list[at]), pre = new Image(); img.setAttribute('data-want', want);
      pre.onload = pre.onerror = function () { if (img.getAttribute('data-want') === want) img.src = want; };
      pre.src = want;
      count.textContent = (at + 1) + ' / ' + list.length;
      count.hidden = list.length < 2; $$('.pzoom__prev, .pzoom__next', box).forEach(function (b) { b.hidden = list.length < 2; });
    }
    function zoomAt(x, y, ns) {
      ns = Math.max(1, Math.min(5, ns));
      var r = img.getBoundingClientRect(), bl = r.left - tx, bt = r.top - ty;
      var lx = (x - bl - tx) / s, ly = (y - bt - ty) / s;
      s = ns; tx = x - bl - lx * s; ty = y - bt - ly * s;
      if (s <= 1.01) { s = 1; tx = 0; ty = 0; }
      paint();
    }
    function build() {
      box = document.createElement('div');
      box.className = 'pzoom'; box.hidden = true;
      box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true'); box.setAttribute('aria-label', 'Product photos');
      box.innerHTML = '<img class="pzoom__img" alt="" draggable="false">' +
        '<button type="button" class="pzoom__btn pzoom__close" aria-label="Close">&times;</button>' +
        '<button type="button" class="pzoom__btn pzoom__prev" aria-label="Previous photo">&lsaquo;</button>' +
        '<button type="button" class="pzoom__btn pzoom__next" aria-label="Next photo">&rsaquo;</button>' +
        '<span class="pzoom__hint">Tap to zoom</span><span class="pzoom__count"></span>';
      document.body.appendChild(box);
      img = $('.pzoom__img', box); count = $('.pzoom__count', box);
      on($('.pzoom__close', box), 'click', close);
      on($('.pzoom__prev', box), 'click', function () { show(at - 1); });
      on($('.pzoom__next', box), 'click', function () { show(at + 1); });
      var pts = {}, start = null, pinch = null;
      function n() { return Object.keys(pts).length; }
      on(img, 'pointerdown', function (e) {
        e.preventDefault(); try { img.setPointerCapture(e.pointerId); } catch (x) {}
        pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        if (n() === 1) start = { x: e.clientX, y: e.clientY, t: Date.now(), tx: tx, ty: ty, moved: false };
        if (n() === 2) {
          var k = Object.keys(pts), a = pts[k[0]], b = pts[k[1]];
          pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: s }; start = null;
        }
      });
      on(img, 'pointermove', function (e) {
        if (!pts[e.pointerId]) return;
        pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        if (pinch && n() >= 2) {
          var k = Object.keys(pts), a = pts[k[0]], b = pts[k[1]];
          zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, pinch.s * Math.hypot(a.x - b.x, a.y - b.y) / pinch.d);
        } else if (start) {
          var dx = e.clientX - start.x, dy = e.clientY - start.y;
          if (Math.abs(dx) + Math.abs(dy) > 8) start.moved = true;
          if (s > 1) { tx = start.tx + dx; ty = start.ty + dy; paint(); }
        }
      });
      function up(e) {
        if (!pts[e.pointerId]) return;
        delete pts[e.pointerId];
        if (pinch) { if (n() < 2) pinch = null; start = null; return; }
        if (!start) return;
        var dx = e.clientX - start.x;
        if (!start.moved) zoomAt(e.clientX, e.clientY, s > 1 ? 1 : 2.5);
        else if (s === 1 && Math.abs(dx) > 60 && list.length > 1) show(at + (dx < 0 ? 1 : -1));
        start = null;
      }
      on(img, 'pointerup', up); on(img, 'pointercancel', up);
      on(box, 'click', function (e) { if (e.target === box) close(); });
      on(document, 'keydown', function (e) {
        if (box.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight') show(at + 1);
        else if (e.key === 'ArrowLeft') show(at - 1);
      });
    }
    function open() {
      if (!box) build();
      collect();
      var cur = main.currentSrc || main.src, i = 0;
      list.forEach(function (u, k) { if (cur && u && cur.split('?')[0].replace(/-p\.webp|-l\.webp/, '') === u.split('?')[0].replace(/-p\.webp|-l\.webp/, '')) i = k; });
      var pressed = $$('[data-thumb]').map(function (b) { return b.getAttribute('aria-pressed') === 'true'; }).indexOf(true);
      if (pressed > -1) i = pressed;
      box.hidden = false; document.documentElement.classList.add('pzoom-open');
      show(i); $('.pzoom__close', box).focus();
    }
    function close() { box.hidden = true; document.documentElement.classList.remove('pzoom-open'); reset(); main.focus && frame.focus(); }
    frame.setAttribute('tabindex', '0'); frame.setAttribute('role', 'button'); frame.setAttribute('aria-label', 'Open photo full screen');
    on(frame, 'click', function (e) {
      if (e.target.closest('[data-spin-open], [data-spin-canvas]')) return;
      if (frame.classList.contains('is-spinning')) return;
      open();
    });
    on(frame, 'keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
  }

  function init() {
    safe('track', pageView); safe('attribution', attribution); safe('checkout', checkoutIntent);
    safe('banner', banner); safe('menu', menu); safe('reveals', reveals); safe('fade', fadeImages);
    safe('tiles', tiles); safe('hero', nightHero); safe('film', film); safe('offscreen', offscreenPause);
    safe('overlays', overlayCore); safe('search', search); safe('subscribe', subscribe);
    safe('cart', cartDrawer); safe('shipbars', shipBars); safe('pdp', pdp); safe('zoom', photoZoom); safe('salehero', saleHero); safe('fangallery', fanGallery); safe('spin', spin); safe('recs', recommendations); safe('recent', recentlyViewed); safe('reviews', reviews); safe('policy', policyChrome);
  }
  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', init); else init();
})();

/* Also available (snippets/buying-options): add a related product without leaving the page. */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bopt-add]'); if (!btn || !window.pookCartAdd) return;
    var row = btn.closest('.bopt'), sel = row && row.querySelector('[data-bopt-variant]');
    var id = btn.getAttribute('data-variant') || (sel && sel.value); if (!id) return;
    btn.disabled = true; var label = btn.textContent; btn.textContent = '…';
    window.pookCartAdd(parseInt(id, 10), 1).catch(function () {}).then(function () { btn.disabled = false; btn.textContent = label; });
  });
})();
